import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { formatApiErrorDetail } from "../lib/api";
import { nextKey } from "../lib/listKeys";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Bot, Plus, Trash2, Save, Globe, Phone, ChevronDown, AlertTriangle, CheckCircle2, Volume2, Play, Loader2 } from "lucide-react";
import { fmtDate } from "../lib/constants";

export default function AIAgent() {
  const qc = useQueryClient();
  const [cfg, setCfg] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [qKeys, setQKeys] = useState([]);

  const { data: config } = useQuery({ queryKey: ["ai-config"], queryFn: () => api.get("/ai/config").then((r) => r.data) });
  const { data: calls = [] } = useQuery({ queryKey: ["ai-calls"], queryFn: () => api.get("/ai/calls").then((r) => r.data), refetchInterval: 8000 });

  useEffect(() => {
    if (!config || cfg) return;
    const questions = config.questions?.length ? config.questions : ["What are you hoping to solve?", "What's your budget range?", "When are you looking to get started?"];
    setQKeys(questions.map(() => nextKey()));
    setCfg({
      agent_name: config.agent_name || "Alex",
      company_name: config.company_name || "",
      kb_url: config.kb_url || "",
      greeting: config.greeting || "",
      voice: config.voice,
      questions,
    });
  }, [config, cfg]);

  const setF = (k, v) => setCfg((c) => ({ ...c, [k]: v }));
  const setQ = (i, v) => setCfg((c) => ({ ...c, questions: c.questions.map((q, idx) => (idx === i ? v : q)) }));
  const addQ = () => { setCfg((c) => ({ ...c, questions: [...c.questions, ""] })); setQKeys((k) => [...k, nextKey()]); };
  const delQ = (i) => {
    setCfg((c) => ({ ...c, questions: c.questions.filter((_, idx) => idx !== i) }));
    setQKeys((k) => k.filter((_, idx) => idx !== i));
  };

  const save = async () => {
    try {
      const { data } = await api.post("/ai/config", cfg);
      toast.success(`Saved${data.kb_chars ? ` · knowledge base: ${data.kb_chars} chars crawled` : ""}`);
      qc.invalidateQueries({ queryKey: ["ai-config"] });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const previewVoice = async () => {
    setPreviewing(true);
    try {
      const text = cfg.greeting?.trim() || `Hi, this is ${cfg.agent_name || "Alex"} from ${cfg.company_name || "our team"}. Do you have a quick minute?`;
      const { data } = await api.post("/ai/voice-preview", { text, voice_id: cfg.voice });
      await new Audio(data.audio).play();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Voice preview failed");
    } finally {
      setPreviewing(false);
    }
  };

  const startCall = async (sid) => {
    try { await api.post(`/ai/calls/${sid}/start`); toast.success("AI agent is dialing..."); qc.invalidateQueries({ queryKey: ["ai-calls"] }); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const delCall = async (sid) => { await api.delete(`/ai/calls/${sid}`); qc.invalidateQueries({ queryKey: ["ai-calls"] }); };

  if (!cfg) return null;
  const queued = calls.filter((c) => c.status === "queued");
  const done = calls.filter((c) => c.status !== "queued");
  const ready = config?.voice_ready && config?.llm_ready;

  return (
    <div data-testid="ai-agent-page" className="space-y-8">
      <div>
        <h1 className="font-display text-3xl font-extrabold tracking-tight flex items-center gap-2"><Bot className="h-7 w-7 text-primary" /> AI Call Agent</h1>
        <p className="text-muted-foreground mt-1">An AI voice agent that calls leads, answers from your knowledge base, and qualifies them automatically.</p>
      </div>

      {!ready && (
        <div className="flex items-center gap-2 text-sm text-amber-400 bg-amber-500/10 border border-amber-500/20 rounded-xl p-3" data-testid="ai-not-ready">
          <AlertTriangle className="h-4 w-4" />
          {!config?.voice_ready ? "Twilio Voice must be configured to place AI calls. " : ""}{!config?.llm_ready ? "LLM key missing." : ""}
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="rounded-2xl border border-border bg-card p-6 space-y-4">
          <h3 className="font-display font-semibold">Agent setup</h3>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2"><Label>Agent name</Label><Input value={cfg.agent_name} onChange={(e) => setF("agent_name", e.target.value)} data-testid="agent-name-input" /></div>
            <div className="space-y-2"><Label>Company name</Label><Input value={cfg.company_name} onChange={(e) => setF("company_name", e.target.value)} data-testid="company-name-input" /></div>
          </div>
          <div className="space-y-2">
            <Label className="flex items-center gap-1.5"><Globe className="h-3.5 w-3.5" /> Knowledge base URL (crawled to train the agent)</Label>
            <Input value={cfg.kb_url} onChange={(e) => setF("kb_url", e.target.value)} placeholder="https://yourcompany.com" data-testid="kb-url-input" />
            {config?.kb_chars > 0 && <p className="text-xs text-emerald-400 flex items-center gap-1"><CheckCircle2 className="h-3 w-3" /> {config.kb_chars} chars loaded</p>}
          </div>
          <div className="space-y-2"><Label>Opening line</Label><Textarea value={cfg.greeting} onChange={(e) => setF("greeting", e.target.value)} rows={2} placeholder="Hi, this is Alex from Acme. Do you have a quick minute?" data-testid="greeting-input" /></div>
          <div className="space-y-2">
            <Label className="flex items-center gap-1.5"><Volume2 className="h-3.5 w-3.5" /> Voice {config?.eleven_ready && <span className="text-xs text-emerald-400 font-normal">· ElevenLabs</span>}</Label>
            <div className="flex gap-2">
              <Select value={cfg.voice} onValueChange={(v) => setF("voice", v)}>
                <SelectTrigger data-testid="voice-select" className="flex-1"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-popover border-border">
                  {(config?.voices || []).map((v) => {
                    const id = v.id || v;
                    return <SelectItem key={id} value={id}>{v.name ? `${v.name} — ${v.desc}` : id}</SelectItem>;
                  })}
                </SelectContent>
              </Select>
              <Button type="button" variant="outline" onClick={previewVoice} disabled={previewing} data-testid="preview-voice-btn" className="shrink-0">
                {previewing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
                <span className="ml-1.5">Preview</span>
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">Hear your opening line in the selected voice before you call.</p>
          </div>
        </div>

        <div className="rounded-2xl border border-border bg-card p-6 space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="font-display font-semibold">Qualification questions</h3>
            <Button size="sm" variant="outline" onClick={addQ} data-testid="add-question"><Plus className="h-4 w-4 mr-1" /> Add</Button>
          </div>
          <p className="text-xs text-muted-foreground">The agent works these into the call naturally, one at a time.</p>
          <div className="space-y-2">
            {cfg.questions.map((q, i) => (
              <div key={qKeys[i] ?? `q-${i}`} className="flex gap-2">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-secondary text-xs font-mono">{i + 1}</span>
                <Input value={q} onChange={(e) => setQ(i, e.target.value)} data-testid={`question-${i}`} />
                <Button size="icon" variant="ghost" onClick={() => delQ(i)}><Trash2 className="h-4 w-4 text-rose-400" /></Button>
              </div>
            ))}
          </div>
        </div>
      </div>

      <Button onClick={save} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="save-ai-config"><Save className="h-4 w-4 mr-2" /> Save agent</Button>

      {queued.length > 0 && (
        <div className="rounded-2xl border border-border bg-card p-6">
          <h3 className="font-display font-semibold mb-4">AI Qualify queue ({queued.length})</h3>
          <div className="space-y-2">
            {queued.map((c) => (
              <div key={c.id} className="flex items-center justify-between rounded-xl border border-border p-3" data-testid={`queued-${c.session_id}`}>
                <div><p className="font-medium">{c.lead_name}</p><p className="text-xs font-mono text-muted-foreground">{c.phone}</p></div>
                <div className="flex gap-2">
                  <Button size="sm" onClick={() => startCall(c.session_id)} className="bg-primary text-primary-foreground" data-testid={`start-${c.session_id}`}><Phone className="h-4 w-4 mr-1" /> Call now</Button>
                  <Button size="icon" variant="ghost" onClick={() => delCall(c.session_id)}><Trash2 className="h-4 w-4 text-rose-400" /></Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="rounded-2xl border border-border bg-card p-6">
        <h3 className="font-display font-semibold mb-4">AI call results</h3>
        {done.length === 0 && <p className="text-sm text-muted-foreground">No AI calls yet. Use "AI Qualify" on a lead to start.</p>}
        <div className="space-y-2">
          {done.map((c) => {
            const qd = c.qualification;
            return (
              <div key={c.id} className="rounded-xl border border-border" data-testid={`ai-call-${c.session_id}`}>
                <button onClick={() => setExpanded(expanded === c.id ? null : c.id)} className="w-full flex items-center justify-between p-4 text-left">
                  <div className="flex items-center gap-3">
                    <div><p className="font-medium">{c.lead_name}</p><p className="text-xs text-muted-foreground">{fmtDate(c.created_at)} · {c.status}</p></div>
                  </div>
                  <div className="flex items-center gap-3">
                    {qd && <span className={`text-xs px-2.5 py-1 rounded-full ${qd.qualified ? "bg-emerald-500/10 text-emerald-400" : "bg-secondary text-muted-foreground"}`}>{qd.qualified ? "Qualified" : "Not qualified"}{qd.score != null ? ` · ${qd.score}` : ""}</span>}
                    <ChevronDown className={`h-4 w-4 transition-transform ${expanded === c.id ? "rotate-180" : ""}`} />
                  </div>
                </button>
                {expanded === c.id && (
                  <div className="px-4 pb-4 space-y-3 border-t border-border pt-3">
                    {qd?.summary && <p className="text-sm">{qd.summary}</p>}
                    {qd?.answers && Object.keys(qd.answers).length > 0 && (
                      <div className="space-y-1">
                        {Object.entries(qd.answers).map(([q, a]) => (
                          <div key={q} className="text-sm"><span className="text-muted-foreground">{q}</span> — {a}</div>
                        ))}
                      </div>
                    )}
                    {c.transcript?.length > 0 && (
                      <div className="rounded-lg bg-background/50 p-3 space-y-1 max-h-64 overflow-y-auto">
                        {c.transcript.map((m, i) => (
                          <p key={i} className="text-sm"><span className={m.role === "agent" ? "text-primary font-medium" : "text-muted-foreground font-medium"}>{m.role === "agent" ? "AI" : "Lead"}:</span> {m.text}</p>
                        ))}
                      </div>
                    )}
                    <Button size="sm" variant="ghost" onClick={() => delCall(c.session_id)} className="text-rose-400"><Trash2 className="h-4 w-4 mr-1" /> Delete</Button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
